import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { DIRECTUS_INSTANCE } from '../../api';
import { Snackbar } from '../../ui/atoms';
import { uploadImageToDirectus } from '../../features/articles';
import { getWorkspaceBillingStatus, createBillingPortal, createSubscriptionCheckout, createTopupCheckout } from '../../services/billingApi';
import { createAccountClient } from '../../services/accountClient';
import TabNavigation from '../../components/UserProfile/TabNavigation';
import SidebarBottomControls from '../../layouts/Sidebar/SidebarBottomControls';
import { useAccountShell } from '../../contexts/AccountShellContext';
import { ProfileTab, AISettingsTab, SecurityTab, WorkspaceTab, SubscriptionTab, McpTab, FORM_FIELDS } from '../../components/UserProfile/tabs';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import writerIcon from '../../img/icons/prosecaptain-writer.svg';
import assistantIcon from '../../img/icons/prosecaptain-assistant.svg';
import {
  PROFILE_BACK_TARGET_KEY,
  getSavedProfileBackTarget
} from '../../utils/profileNavigation';

const TAB_HASH_MAP = {
  '#profile': 'profile',
  '#subscription': 'subscription',
  '#ai': 'ai',
  '#workspace': 'workspace',
  '#mcp': 'mcp',
  '#security': 'security',
};

// Map tab IDs to URL hashes
const TAB_URL_MAP = {
  'profile': '#profile',
  'subscription': '#subscription',
  'ai': '#ai',
  'workspace': '#workspace',
  'mcp': '#mcp',
  'security': '#security',
};

const accountClient = createAccountClient({
  directusBaseUrl: DIRECTUS_INSTANCE,
  billingBaseUrl: process.env.REACT_APP_BILLING_API_URL || 'http://localhost:4010',
  getAccessToken: () => localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token')
});

export default function UserProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    userNavigation,
    onSignOut,
    isNotificationsDrawerOpen,
    onToggleNotificationsDrawer
  } = useAccountShell();
  const appOrigin = window.location.origin;
  const [formData, setFormData] = useState(Object.fromEntries(FORM_FIELDS.map(field => [field.name, ''])));
  const [workspaceName, setWorkspace] = useState('');
  const [workspaceId, setWorkspaceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('');
  const [billingStatus, setBillingStatus] = useState(null);

  const getErrorMessage = (error, fallback) => {
    return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
  };

  const isTrustedBackUrl = (candidate) => {
    if (!candidate) return false;
    try {
      const parsed = new URL(candidate, window.location.origin);
      if (!/^https?:$/.test(parsed.protocol)) return false;
      const host = parsed.hostname || '';
      return (
        host === 'localhost' ||
        host === '127.0.0.1' ||
        host === window.location.hostname ||
        host.endsWith('.prosecaptain.com')
      );
    } catch (_) {
      return false;
    }
  };

  const getBackUrl = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get('return_to') || params.get('back_to');
    if (isTrustedBackUrl(returnTo)) {
      try {
        sessionStorage.setItem(PROFILE_BACK_TARGET_KEY, returnTo);
      } catch (_) {
        // ignore storage errors
      }
      return returnTo;
    }

    const fromApp = params.get('from_app');
    if (fromApp === 'assistant') {
      const target = `${appOrigin}/agent/ai-agents`;
      try {
        sessionStorage.setItem(PROFILE_BACK_TARGET_KEY, target);
      } catch (_) {}
      return target;
    }
    if (fromApp === 'writer') {
      const target = `${appOrigin}/writer/articles`;
      try {
        sessionStorage.setItem(PROFILE_BACK_TARGET_KEY, target);
      } catch (_) {}
      return target;
    }

    if (document.referrer && isTrustedBackUrl(document.referrer)) {
      try {
        sessionStorage.setItem(PROFILE_BACK_TARGET_KEY, document.referrer);
      } catch (_) {}
      return document.referrer;
    }

    try {
      const savedTarget = getSavedProfileBackTarget();
      if (savedTarget && savedTarget.startsWith('/')) return savedTarget;
      if (isTrustedBackUrl(savedTarget)) return savedTarget;
    } catch (_) {
      // ignore storage errors
    }

    return `${appOrigin}/writer/articles`;
  }, [appOrigin]);

  const backUrl = useMemo(() => getBackUrl(), [getBackUrl]);
  const appTargets = useMemo(() => ([
    { key: 'assistant', label: t('userProfile.apps.assistant'), url: `${appOrigin}/agent/ai-agents` },
    { key: 'writer', label: t('userProfile.apps.writer'), url: `${appOrigin}/writer/articles` }
  ]), [appOrigin, t]);
  const defaultAppUrl = useMemo(() => {
    const matched = appTargets.find((app) => backUrl.startsWith(app.url));
    return matched?.url || backUrl || appTargets[0]?.url || `${appOrigin}/writer/articles`;
  }, [appOrigin, appTargets, backUrl]);
  const [selectedAppUrl, setSelectedAppUrl] = useState(defaultAppUrl);
  const [isAppMenuOpen, setIsAppMenuOpen] = useState(false);
  const appMenuRef = useRef(null);

  useEffect(() => {
    setSelectedAppUrl(defaultAppUrl);
  }, [defaultAppUrl]);

  const handleAppSelect = (url) => {
    setSelectedAppUrl(url);
    setIsAppMenuOpen(false);
    if (url) {
      window.location.href = url;
    }
  };

  const handleBack = useCallback(() => {
    const savedTarget = getSavedProfileBackTarget();
    if (savedTarget?.startsWith('/')) {
      navigate(savedTarget);
      return;
    }
    if (isTrustedBackUrl(savedTarget)) {
      window.location.href = savedTarget;
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    window.location.href = backUrl;
  }, [navigate, backUrl]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (appMenuRef.current && !appMenuRef.current.contains(event.target)) {
        setIsAppMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const refreshBillingStatus = async (workspaceId) => {
    if (!workspaceId) return;
    try {
      const billing = await getWorkspaceBillingStatus(Number(workspaceId));
      setBillingStatus(billing);
    } catch (_) {
      // Billing API is optional, profile should still load.
    }
  };
  
  // Initialize active tab from URL hash or default to 'profile'
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash;
    return TAB_HASH_MAP[hash] || 'profile';
  });

  // Update URL hash when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    window.location.hash = TAB_URL_MAP[tabId];
  };

  // Listen for hash changes in the URL
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      const tabId = TAB_HASH_MAP[hash];
      if (tabId) {
        setActiveTab(tabId);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
      if (!token) return;

      try {
        const userData = await accountClient.getCurrentUser({
          fields: [
            '*',
            'linked_workspace.*',
            'avatar.*',
            'subscription_status',
            'subscription_plan',
            'subscription_period_end',
            'subscription_id',
            'language'
          ]
        });
        const avatarUrl = userData.avatar ? `${DIRECTUS_INSTANCE}/assets/${userData.avatar.id}` : null;
        if (userData.linked_workspace) {
          const workspaceData = await accountClient.getWorkspaceById(userData.linked_workspace.id, {
            fields: ['*', 'assistant_prompt', 'widget_token']
          });
          
          setFormData(prevData => ({
            ...prevData,
            firstName: userData.first_name || '',
            lastName: userData.last_name || '',
            email: userData.email || '',
            language: userData.language || 'en-US',
            description: userData.description || '',
            location: userData.location || '',
            title: userData.title || '',
            position: userData.position || '',
            goals: userData.goals || '',
            avatar: avatarUrl,
            assistant_prompt: workspaceData?.assistant_prompt || '',
            widget_token: workspaceData?.widget_token || '',
            subscription_status: userData.subscription_status || 'none',
            subscription_plan: userData.subscription_plan || null,
            subscription_period_end: userData.subscription_period_end ? new Date(userData.subscription_period_end) : null,
            subscription_id: userData.subscription_id || null
          }));

          setWorkspace(userData.linked_workspace.workspace_name || t('common.noWorkspaceName'));
          setWorkspaceId(userData.linked_workspace.id || null);
          await refreshBillingStatus(userData.linked_workspace.id);
        } else {
          setWorkspaceId(null);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError(t('profile.errors.loadUserData'));
        setWorkspace(t('common.errorLoadingWorkspace'));
      }
    };

    fetchUserData();
  }, [t]);

  // Note: Language initialization is now handled in App.js to avoid conflicts

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const token = localStorage.getItem('directus_token');
    if (!token) {
      setError(t('common.noToken'));
      setLoading(false);
      return;
    }

    try {
      const userFields = {};
      const workspaceFields = {};
      
      // widget_token is read-only for users (Directus policy), never sent on save.
      const workspaceFieldNames = ['assistant_prompt'];
      
      // Map form field names to Directus API field names
      const fieldMapping = {
        firstName: 'first_name',
        lastName: 'last_name',
        language: 'language',
        title: 'title',
        position: 'position',
        description: 'description',
        goals: 'goals'
      };

      // email is not editable here: Directus policy denies it (change goes through support/verification).
      Object.keys(formData).forEach(key => {
        if (workspaceFieldNames.includes(key)) {
          workspaceFields[key] = formData[key];
        } else if (fieldMapping[key]) {
          userFields[fieldMapping[key]] = formData[key];
        }
      });
      
      console.log('Updating user with fields:', userFields);
      
      await accountClient.updateCurrentUser(userFields);
      const me = await accountClient.getCurrentUser({ fields: ['linked_workspace.id'] });
      const workspaceId = me?.linked_workspace?.id;
      
      if (workspaceId && Object.keys(workspaceFields).length > 0) {
        await accountClient.updateWorkspace(workspaceId, workspaceFields);
      }
      
      // Update i18n language if it was changed
      if (formData.language) {
        const languageCode = formData.language.split('-')[0]; // Convert 'en-US' to 'en'
        i18n.changeLanguage(languageCode);
        localStorage.setItem('language', languageCode);
      }
      
      setSnackbarMessage(t('profile.success.profileUpdated'));
      setSnackbarType('success');
      setShowSnackbar(true);
      setTimeout(() => setShowSnackbar(false), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbarMessage(t('profile.errors.updateProfile'));
      setSnackbarType('error');
      setShowSnackbar(true);
      setTimeout(() => setShowSnackbar(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    try {
      const uploadResponse = await uploadImageToDirectus(file);
      if (uploadResponse.success) {
        await accountClient.updateCurrentUser({
          avatar: uploadResponse.file.id
        });

        const avatarUrl = uploadResponse.file.url;
        setFormData(prev => ({
          ...prev,
          avatar: avatarUrl
        }));
        localStorage.setItem('user_avatar', avatarUrl);

        return uploadResponse.file;
      } else {
        throw new Error(uploadResponse.error);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      const result = await createBillingPortal({ workspaceId: Number(workspaceId) });
      if (result?.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error, 'Failed to open billing portal'));
      setSnackbarType('error');
      setShowSnackbar(true);
    }
  };

  const handleStartTopup = async (topupPriceId) => {
    try {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      const result = await createTopupCheckout({
        workspaceId: Number(workspaceId),
        topupPriceId,
        successUrl: `${window.location.origin}/profile#subscription`,
        cancelUrl: `${window.location.origin}/profile#subscription`
      });
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error, 'Failed to start top-up checkout'));
      setSnackbarType('error');
      setShowSnackbar(true);
    }
  };

  const handleStartSubscription = async (selectedPriceId = null) => {
    try {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }

      if (!selectedPriceId) {
        throw new Error('Subscription price is not configured');
      }

      const result = await createSubscriptionCheckout({
        workspaceId: Number(workspaceId),
        priceId: selectedPriceId,
        successUrl: `${window.location.origin}/subscription-success`,
        cancelUrl: `${window.location.origin}/profile#subscription`
      });

      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error, 'Failed to start subscription checkout'));
      setSnackbarType('error');
      setShowSnackbar(true);
    }
  };

  useEffect(() => {
    if (activeTab !== 'subscription') return;
    if (workspaceId) {
      refreshBillingStatus(workspaceId);
    }
  }, [activeTab, workspaceId]);

  const appIcons = {
    assistant: assistantIcon,
    writer: writerIcon
  };

  return (
    <div className="relative min-h-[calc(100vh-var(--app-top-offset,0px))]">
      <aside
        className="hidden lg:flex lg:flex-col fixed z-20 w-64 border-r border-gray-200 bg-neutral-100 px-4 py-6 pr-0"
        style={{
          top: 'var(--app-top-offset, 0px)',
          height: 'calc(100vh - var(--app-top-offset, 0px))'
        }}
      >
        <div className="mb-5 mt-1 flex items-center gap-2">
          <button
            type="button"
            onClick={handleBack}
            className="flex-1 inline-flex items-center rounded-lg px-2 py-2 text-sm font-medium text-gray-700 hover:bg-white transition-colors"
          >
            {`← ${t('common.back', { defaultValue: 'Powrót' })}`}
          </button>
          <div ref={appMenuRef} className="relative shrink-0">
            <button
              type="button"
              onClick={() => setIsAppMenuOpen((prev) => !prev)}
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                isAppMenuOpen
                  ? 'bg-white text-gray-900 shadow-sm ring-1 ring-neutral-200'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-white'
              }`}
              title={t('navigation.apps', { defaultValue: 'Apps' })}
              aria-label={t('navigation.apps', { defaultValue: 'Apps' })}
              aria-expanded={isAppMenuOpen}
            >
              <Squares2X2Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
            </button>

            {isAppMenuOpen && (
              <div className="absolute left-0 top-full z-30 mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                {appTargets.map((app) => {
                  const iconSrc = appIcons[app.key] || assistantIcon;
                  const isActive = selectedAppUrl === app.url;
                  return (
                    <button
                      key={app.key}
                      type="button"
                      onClick={() => handleAppSelect(app.url)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left ${
                        isActive ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <img src={iconSrc} alt={app.label} className="h-5 w-5 object-contain" />
                      <span>{app.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <TabNavigation activeTab={activeTab} setActiveTab={handleTabChange} />
        </div>
        <div className="mt-auto border-t border-gray-200 pt-4 pb-1">
          <SidebarBottomControls
            layout="row"
            isNotificationsDrawerOpen={isNotificationsDrawerOpen}
            onToggleNotificationsDrawer={onToggleNotificationsDrawer}
            userNavigation={userNavigation}
            username={formData.firstName}
            lastName={formData.lastName}
            workspaceName={workspaceName}
            onSignOut={onSignOut}
          />
        </div>
      </aside>

      <div className="lg:ml-64 min-w-0">
      <section className="min-w-0 bg-neutral-100 p-4">
        <div className="min-h-full rounded-xl border border-neutral-200 bg-white p-6">
        {activeTab === 'profile' && (
          <form onSubmit={handleSubmit}>
            <ProfileTab 
              formData={formData} 
              handleChange={handleChange} 
              handleImageUpload={handleImageUpload}
              loading={loading}
              setSnackbarMessage={setSnackbarMessage}
              setSnackbarType={setSnackbarType}
              setShowSnackbar={setShowSnackbar}
            />
            {error && <p className="text-red-500 mt-4">{error}</p>}
            <div className="mt-6 flex items-center justify-end gap-x-6">
              <button type="button" className="text-sm font-semibold leading-6 text-gray-900">
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50"
              >
                {loading ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        )}
        
        {activeTab === 'subscription' && (
          <SubscriptionTab
            formData={formData}
            billingStatus={billingStatus}
            handleOpenBillingPortal={handleOpenBillingPortal}
            handleStartSubscription={handleStartSubscription}
            handleStartTopup={handleStartTopup}
          />
        )}
        
        {activeTab === 'ai' && (
          <form onSubmit={handleSubmit}>
            <AISettingsTab 
              formData={formData} 
              setFormData={setFormData} 
              activeTab={activeTab}
            />
            {error && <p className="text-red-500 mt-4">{error}</p>}
            <div className="mt-6 flex items-center justify-end gap-x-6">
              <button type="button" className="text-sm font-semibold leading-6 text-gray-900">
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50"
              >
                {loading ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        )}
        
        {activeTab === 'workspace' && (
          <WorkspaceTab formData={formData} />
        )}

        {activeTab === 'mcp' && <McpTab />}
        
        {activeTab === 'security' && (
          <SecurityTab 
            formData={formData} 
            loading={loading}
            setLoading={setLoading}
            setSnackbarMessage={setSnackbarMessage}
            setSnackbarType={setSnackbarType}
            setShowSnackbar={setShowSnackbar}
          />
        )}
        
      </div>
      </section>
      </div>
      
      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
        />
      )}
    </div>
  );
}